import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';

import {
    Button,
    IconButton,
    Paper,
    Typography,
    Checkbox,
    FormGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    RadioGroup,
    Radio,
} from '@material-ui/core';

class ExportImagesModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            exportTables: true,
            exportCharts: false,
            exportFormat: 'png', // 'png' | 'svg'
            withBackground: true
        };
    }

    componentDidUpdate(prevProps) {
        // When modal opens, reset selection to default
        if (!prevProps.show && this.props.show) {
            this.setState({
                exportTables: true,
                exportCharts: false,
                exportFormat: 'png',
                withBackground: true
            });
        }
    }

    onCloseModal = () => {
        this.props.onClose();
    };

    onToggleTables = () => {
        this.setState((prev) => ({ exportTables: !prev.exportTables }));
    };

    onToggleCharts = () => {
        this.setState((prev) => ({ exportCharts: !prev.exportCharts }));
    };

    onToggleWithBackground = () => {
        this.setState((prev) => ({ withBackground: !prev.withBackground }));
    };

    onChangeFormat = (event) => {
        this.setState({ exportFormat: event.target.value });
    };

    onConfirm = () => {
        const { exportTables, exportCharts, exportFormat, withBackground } = this.state;
        const { onConfirm, hasSelection } = this.props;

        if (typeof onConfirm === 'function') {
            onConfirm({
                exportTables: hasSelection ? exportTables : true,
                exportCharts: hasSelection ? exportCharts : true,
                format: exportFormat,
                withBackground: withBackground,
            });
        }
    };

    render() {
        const { show, classes } = this.props;
        const { exportTables, exportCharts, exportFormat, withBackground } = this.state;

        if (!show) {
            return null;
        }

        const nothingSelected = !exportTables && !exportCharts;

        return (
            <div className={classes.container}>
                <Paper className={classes.modal}>
                    <div className={classes.header}>
                        <Typography className={classes.title} variant="title">
                            Export project images
                        </Typography>
                        <IconButton
                            aria-label="Close modal"
                            onClick={this.onCloseModal}
                        >
                            x
                        </IconButton>
                    </div>

                    <div className={classes.content}>
                        {!this.props.hasSelection && <Typography
                            className={classes.subtitle}
                            variant="subheading"
                        >
                            Choose what you want to export
                        </Typography>}

                        {!this.props.hasSelection && <FormGroup className={classes.checkboxGroup}>
                            <div className={classes.controlSection}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={exportTables}
                                            onChange={this.onToggleTables}
                                            color="primary"
                                        />
                                    }
                                    label="Tables"
                                />
                                {/* Enable when we support chart exports */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={exportCharts}
                                            onChange={this.onToggleCharts}
                                            color="primary"
                                            disabled
                                        />
                                    }
                                    label="Charts"
                                />
                            </div>
                        </FormGroup>}

                        <FormControl
                            component="fieldset"
                            className={classes.formatControl}
                        >
                            <Typography
                                className={classes.subtitle}
                                variant="subheading"
                            >
                                Export format
                            </Typography>
                            <div className={classes.controlSection}>
                                <RadioGroup
                                    aria-label="Export format"
                                    name="exportFormat"
                                    value={exportFormat}
                                    onChange={this.onChangeFormat}
                                    row
                                >
                                    <FormControlLabel
                                        value="png"
                                        control={<Radio color="primary" />}
                                        label="PNG"
                                    />
                                    <FormControlLabel
                                        value="svg"
                                        control={<Radio color="primary" />}
                                        label="SVG"
                                    />
                                </RadioGroup>
                                {exportFormat === "png" && <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={withBackground}
                                            onChange={this.onToggleWithBackground}
                                            color="primary"
                                        />
                                    }
                                    label="Export with background"
                                />}
                            </div>
                        </FormControl>
                    </div>

                    <div className={classes.footer}>
                        <Button
                            onClick={this.onCloseModal}
                            color="default"
                            className={classes.cancelButton}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={this.onConfirm}
                            disabled={nothingSelected}
                            variant="contained"
                            color="primary"
                        >
                            Continue
                        </Button>
                    </div>
                </Paper>
                <div
                    className={classes.background}
                    onClick={this.onCloseModal}
                />
            </div>
        );
    }
}

ExportImagesModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    hasSelection: PropTypes.bool.isRequired,
};

ExportImagesModal.defaultProps = {
    show: false,
    hasSelection: false,
};

const style = ({ palette, typography }) => ({
    container: {
        width: 0,
        height: 0,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '20px',
        borderBottom: `1px solid ${palette.background.gray2Color}`,
    },
    title: {
        lineHeight: '1.8em',
    },
    subtitle: {
        marginBottom: '16px',
    },
    content: {
        padding: '16px 20px 0 20px',
    },
    checkboxGroup: {
        marginBottom: '16px',
    },
    formatControl: {
        marginBottom: '16px',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '15px 20px',
        borderTop: `1px solid ${palette.background.gray2Color}`,
    },
    cancelButton: {
        marginRight: '8px',
        textTransform: 'none',
    },
    background: {
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(0, 0, 0, 0.5)',
    },
    modal: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: '50%',
        maxWidth: '550px',
        minWidth: '300px',
        transform: 'translate(-50%, -50%)',
        zIndex: 128,
    },
    controlSection: {
        padding: "0 1rem"
    }
});

export default injectSheet(style)(ExportImagesModal);



// WEBPACK FOOTER //
// ./src/components/modals/ExportImagesModal.js